import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { CheckCircle2, ChevronLeft, Info, Key, Loader2, Lock, Plus, Save, Trash2, Unlock } from "lucide-react";
import { API } from "@/constants";
import Disclaimer from "@/components/shared/Disclaimer";
import Footer from "@/components/shared/Footer";

const SecretsManagerPage = () => {
  const [secrets, setSecrets] = useState([]);
  const [services, setServices] = useState({});
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showAddForm, setShowAddForm] = useState(false);
  const [newSecret, setNewSecret] = useState({ service_name: '', api_key: '', description: '' });
  const [saving, setSaving] = useState(false);
  
  const fetchData = async () => {
    try {
      const [secretsRes, servicesRes, statusRes] = await Promise.all([
        axios.get(`${API}/secrets`),
        axios.get(`${API}/secrets/services`),
        axios.get(`${API}/secrets/status`)
      ]);
      setSecrets(secretsRes.data.secrets || []);
      setServices(servicesRes.data.services || {});
      setStatus(statusRes.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };
  
  useEffect(() => { fetchData(); }, []);
  
  const saveSecret = async () => {
    if (!newSecret.service_name || !newSecret.api_key) return;
    setSaving(true);
    try {
      await axios.post(`${API}/secrets`, newSecret);
      setNewSecret({ service_name: '', api_key: '', description: '' });
      setShowAddForm(false);
      fetchData();
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  };
  
  const deleteSecret = async (serviceName) => {
    if (!window.confirm(`Delete API key for ${serviceName}?`)) return;
    try {
      await axios.delete(`${API}/secrets/${serviceName}`);
      fetchData();
    } catch (e) {
      console.error(e);
    }
  };
  
  return (
    <main className="min-h-screen bg-slate-50" data-testid="secrets-manager-page">
      <Disclaimer />
      <div className="container-shell py-8 max-w-4xl">
        <div className="mb-6">
          <Link to="/tools" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1 mb-4">
            <ChevronLeft className="h-4 w-4" /> Back to Tools
          </Link>
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold flex items-center gap-3" style={{ fontFamily: "'Fraunces', serif" }}>
                <Key className="h-8 w-8" /> API Key Manager
              </h1>
              <p className="text-muted-foreground">Securely store API keys for external services like Perplexity and Grok</p>
            </div>
            <Button onClick={() => setShowAddForm(true)} className="gap-2">
              <Plus className="h-4 w-4" /> Add API Key
            </Button>
          </div>
        </div>
        
        {/* Status Card */}
        {status && (
          <Card className={`mb-6 ${status.ready_for_investigator ? 'border-emerald-500 bg-emerald-50' : 'border-amber-500 bg-amber-50'}`}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                {status.ready_for_investigator ? (
                  <Unlock className="h-6 w-6 text-emerald-600" />
                ) : (
                  <Lock className="h-6 w-6 text-amber-600" />
                )}
                <div>
                  <h3 className={`font-semibold ${status.ready_for_investigator ? 'text-emerald-800' : 'text-amber-800'}`}>
                    {status.ready_for_investigator ? 'The Investigator is Ready' : 'The Investigator Requires Setup'}
                  </h3>
                  <p className={`text-sm ${status.ready_for_investigator ? 'text-emerald-700' : 'text-amber-700'}`}>
                    {status.ready_for_investigator 
                      ? 'Intelligence services are configured. You can use The Investigator tool.'
                      : `Missing API keys: ${status.missing_required?.join(', ') || 'Perplexity or Grok'}`}
                  </p>
                </div>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Add Form */}
        {showAddForm && (
          <Card className="mb-6 animate-fade-in">
            <CardHeader>
              <CardTitle>Add API Key</CardTitle>
              <CardDescription>Select a service and enter your API key. Keys are encrypted at rest.</CardDescription>
            </CardHeader>
            <CardContent className="space-y-4">
              <div className="space-y-1.5">
                <Label>Service</Label>
                <Select value={newSecret.service_name} onValueChange={v => setNewSecret(s => ({...s, service_name: v}))}>
                  <SelectTrigger><SelectValue placeholder="Select service..." /></SelectTrigger>
                  <SelectContent>
                    {Object.entries(services).map(([key, svc]) => (
                      <SelectItem key={key} value={key}>
                        <span className="font-medium">{svc.name}</span>
                        <span className="text-muted-foreground ml-2 text-xs">{svc.description}</span>
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              
              {newSecret.service_name && services[newSecret.service_name] && (
                <Alert>
                  <Info className="h-4 w-4" />
                  <AlertDescription>
                    Get your API key from: <a href={services[newSecret.service_name].docs_url} target="_blank" rel="noopener noreferrer" className="underline text-primary">{services[newSecret.service_name].docs_url}</a>
                  </AlertDescription>
                </Alert>
              )}
              
              <div className="space-y-1.5">
                <Label>API Key</Label>
                <Input 
                  type="password"
                  placeholder="Enter your API key..."
                  value={newSecret.api_key}
                  onChange={e => setNewSecret(s => ({...s, api_key: e.target.value}))}
                />
              </div>
              
              <div className="space-y-1.5">
                <Label>Description (optional)</Label>
                <Input 
                  placeholder="e.g., Production key, Test key..."
                  value={newSecret.description}
                  onChange={e => setNewSecret(s => ({...s, description: e.target.value}))}
                />
              </div>
              
              <div className="flex gap-2">
                <Button onClick={saveSecret} disabled={saving || !newSecret.service_name || !newSecret.api_key}>
                  {saving ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
                  Save Key
                </Button>
                <Button variant="outline" onClick={() => setShowAddForm(false)}>Cancel</Button>
              </div>
            </CardContent>
          </Card>
        )}
        
        {/* Stored Keys */}
        <Card>
          <CardHeader>
            <CardTitle>Stored API Keys</CardTitle>
          </CardHeader>
          <CardContent>
            {loading ? (
              <div className="flex justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : secrets.length === 0 ? (
              <div className="text-center py-8">
                <Key className="h-10 w-10 text-muted-foreground mx-auto mb-3" />
                <p className="text-muted-foreground">No API keys stored yet</p>
                <Button variant="outline" className="mt-3" onClick={() => setShowAddForm(true)}>Add Your First Key</Button>
              </div>
            ) : (
              <div className="space-y-3">
                {secrets.map(secret => (
                  <div key={secret.service_name} className="flex items-center justify-between p-4 rounded-lg bg-slate-50 border">
                    <div className="flex items-center gap-4">
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center">
                        <Key className="h-5 w-5 text-primary" />
                      </div>
                      <div>
                        <h4 className="font-medium">{services[secret.service_name]?.name || secret.service_name}</h4>
                        <p className="text-xs text-muted-foreground">
                          Key: {secret.masked_key} • {secret.description || 'No description'}
                        </p>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      {secret.is_valid ? (
                        <Badge className="bg-emerald-100 text-emerald-800">Valid</Badge>
                      ) : (
                        <Badge variant="destructive">Invalid</Badge>
                      )}
                      <Button variant="ghost" size="sm" onClick={() => deleteSecret(secret.service_name)}>
                        <Trash2 className="h-4 w-4 text-red-500" />
                      </Button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
        
        {/* Service Info */}
        <Card className="mt-6">
          <CardHeader>
            <CardTitle>Supported Services</CardTitle>
            <CardDescription>These services power The Investigator and other premium features</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="grid gap-4 md:grid-cols-2">
              {Object.entries(services).map(([key, svc]) => (
                <div key={key} className="p-3 rounded-lg border bg-white">
                  <div className="flex items-start justify-between">
                    <div>
                      <h4 className="font-medium">{svc.name}</h4>
                      <p className="text-xs text-muted-foreground">{svc.description}</p>
                    </div>
                    {secrets.find(s => s.service_name === key) ? (
                      <CheckCircle2 className="h-5 w-5 text-emerald-500" />
                    ) : (
                      <Badge variant="outline" className="text-xs">Not configured</Badge>
                    )}
                  </div>
                  <div className="mt-2">
                    <span className="text-xs text-muted-foreground">Used by: </span>
                    {svc.required_for.map(tool => (
                      <Badge key={tool} variant="secondary" className="text-xs mr-1">{tool}</Badge>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
      <Footer />
    </main>
  );
};


export default SecretsManagerPage;
